import type { SideNavigationProps } from '@amzn/awsui-components-core/side-navigation';
import type { NavigationReport, StarGridNavGroup } from 'src/components/star-grid/navigation.types';
import { UNGROUPED_NAV_GROUP_ID } from 'src/components/star-grid/utils/starGridNavGroups';
import { reportHref } from 'src/components/star-grid/utils/starGridNavigation';

/**
 * A nav section tagged with the group it renders.
 *
 * Cloudscape hands the item object straight back on `onChange`, and nothing on a plain `Section`
 * identifies it, so the id rides along on the item. Extra properties on a nav item are supported.
 */
export interface StarGridNavSection extends SideNavigationProps.Section {
  readonly groupId: string;
}

export interface StarGridNavItemsInput {
  readonly groups: readonly StarGridNavGroup[];
  /** The whole visible catalog — `reportHref` needs it to spot duplicate names across sections. */
  readonly allReports: readonly NavigationReport[];
  /** Sections to render collapsed. Expansion is otherwise user-controlled; the active report's group
   * is auto-expanded by the hook (which drops it from this list), not forced here. */
  readonly collapsedGroupIds: readonly string[];
  readonly label: (groupId: string) => string;
  /** Heading for the one Reports row. */
  readonly reportsText: string;
  /** Where the Reports row itself navigates — a link group's label is a link. */
  readonly reportsHref: string;
}

const reportLinks = (group: StarGridNavGroup, allReports: readonly NavigationReport[]): SideNavigationProps.Link[] =>
  group.reports.map((report) => ({
    type: 'link',
    text: report.report_name,
    href: reportHref(report, allReports)
  }));

/**
 * One collapsible group of reports, nested inside the Reports row.
 *
 * Expansion follows the user's own collapse state. The active report's group is auto-expanded by the
 * hook, which drops it from `collapsedGroupIds` for one render, so a later user collapse still wins.
 */
const groupSection = (group: StarGridNavGroup, input: StarGridNavItemsInput): StarGridNavSection => ({
  type: 'section',
  groupId: group.id,
  text: input.label(group.id),
  defaultExpanded: !input.collapsedGroupIds.includes(group.id),
  items: reportLinks(group, input.allReports)
});

/**
 * A single catch-all group IS the ungrouped nav, so it is flattened: nesting "Other Reports" inside
 * "Reports" adds a level that separates nothing. This is the shape a deployment with no template
 * information on its reports renders, and it matches what the nav looked like before grouping existed.
 */
const isSoleCatchAll = (groups: readonly StarGridNavGroup[]): boolean => groups.length === 1 && groups[0].id === UNGROUPED_NAV_GROUP_ID;

/**
 * The reports as ONE nav row — "Reports" — with the grouping nested inside it.
 *
 * The page renders only its `items`, inside a collapsible "Reports" block that scrolls on its own and
 * carries the organize control; `text` and `href` describe the row for any caller that wants it whole.
 *
 * Returns `undefined` when there are no reports, so the caller leaves the row out rather than
 * rendering an empty "Reports".
 */
export const starGridReportsNavGroup = (input: StarGridNavItemsInput): SideNavigationProps.LinkGroup | undefined => {
  if (input.groups.length === 0) return undefined;
  return {
    type: 'link-group',
    text: input.reportsText,
    href: input.reportsHref,
    items: isSoleCatchAll(input.groups) ? reportLinks(input.groups[0], input.allReports) : input.groups.map((group) => groupSection(group, input))
  };
};

/**
 * The section id Cloudscape's `onChange` detail refers to, or undefined for any other nav section.
 *
 * Read through `unknown` because `groupId` is an extra property Cloudscape's own item types do not
 * declare, and the detail can also be an expandable link group that has no section shape at all.
 */
export const navSectionGroupId = (item: SideNavigationProps.ChangeDetail['item']): string | undefined => {
  const groupId = (item as unknown as { groupId?: unknown }).groupId;
  return typeof groupId === 'string' ? groupId : undefined;
};
